import { useState } from "react";
import { ChevronRight, Inbox } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { IterationView } from "@/application/use-cases/b2c/build-b2c-board";
import type { BacklogActions } from "@/presentation/components/b2c/backlog-actions";
import { BacklogRow } from "@/presentation/components/b2c/backlog-row";
import type { B2cTaskRow } from "@/infrastructure/supabase/b2c-board.types";

export function BacklogPanel({
  tasks,
  target,
  canWrite,
  actions,
}: {
  tasks: B2cTaskRow[];
  target: IterationView | null;
  canWrite: boolean;
  actions: BacklogActions;
}) {
  const [expanded, setExpanded] = useState(false);

  if (tasks.length === 0) return null;

  const summary = `${tasks.length} task${tasks.length === 1 ? "" : "s"} not in an iteration`;

  return (
    <section className="border-border/60 flex flex-col gap-1.5 rounded-lg border border-dashed p-3">
      <h4>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Hide" : "Show"} the backlog, ${tasks.length} not in an iteration`}
          className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 text-[11px]"
        >
          <Inbox className="size-3.5 shrink-0" aria-hidden />
          <span className="font-data font-semibold">Backlog</span>
          <span>· {summary}</span>
          <ChevronRight
            className={cn(
              "ml-auto size-3.5 shrink-0 transition-transform",
              expanded && "rotate-90"
            )}
            aria-hidden
          />
        </button>
      </h4>

      {expanded ? (
        <ul className="flex flex-col">
          {tasks.map((task) => (
            <BacklogRow
              key={task.id}
              task={task}
              target={target}
              canWrite={canWrite}
              actions={actions}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
